import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { ChevronLeft, Mail } from "lucide-react";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { SafecheckLogo } from "../components/SafecheckLogo";
import { requestPasswordReset } from "../api/authApi";

const inputClass =
  "h-12 w-full rounded-xl border border-border bg-card-muted pl-11 pr-4 text-sm text-foreground outline-none transition-colors placeholder:text-muted/70 focus:border-accent focus:ring-2 focus:ring-accent/20";

export function ForgotPasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [debugCode, setDebugCode] = useState<string | null>(null);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setMessage(null);
    setDebugCode(null);
    setSubmitting(true);

    try {
      const result = await requestPasswordReset(email.trim());
      setMessage(result.message);
      if (result.debug_code) {
        setDebugCode(result.debug_code);
      }
      if (result.proceed_to_reset || result.debug_code) {
        const params = new URLSearchParams({ email: email.trim() });
        if (result.message) {
          params.set("info", result.message);
        }
        navigate(`/login/reset?${params.toString()}`);
      }
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") {
        setError("Délai dépassé. La messagerie met trop de temps à répondre — contactez un super administrateur.");
      } else {
        setError(err instanceof Error ? err.message : "Demande impossible.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-white px-5 py-10">
      <div className="mx-auto w-full max-w-[400px]">
        <div className="mb-8 flex justify-center">
          <SafecheckLogo size="lg" linkToDashboard={false} />
        </div>
        <h1 className="text-2xl font-bold text-foreground">Mot de passe oublié</h1>
        <p className="mt-2 text-sm text-muted">
          Saisissez votre email. Si la messagerie est configurée, un code à 6 chiffres vous sera
          envoyé (valable 30 minutes).
        </p>

        <form onSubmit={handleSubmit} className="theme-card theme-card-static mt-6 space-y-4 p-6">
          <div>
            <label htmlFor="forgot-email" className="mb-2 block text-xs font-semibold uppercase text-muted">
              {t.safecheck.login.email}
            </label>
            <div className="relative">
              <Mail size={18} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-primary" />
              <input
                id="forgot-email"
                type="email"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                className={inputClass}
                placeholder="votre@email.com"
              />
            </div>
          </div>

          {message ? <p className="text-sm text-green-700">{message}</p> : null}
          {debugCode ? (
            <p className="rounded-lg border border-yellow/40 bg-yellow-soft px-3 py-2 text-sm">
              Code dev (local) : <strong>{debugCode}</strong>
            </p>
          ) : null}
          {error ? <p className="text-sm text-red-600">{error}</p> : null}

          <button
            type="submit"
            disabled={submitting}
            className="h-11 w-full rounded-xl bg-accent text-sm font-semibold text-accent-foreground disabled:opacity-60"
          >
            {submitting ? "Envoi…" : "Recevoir un code"}
          </button>
        </form>

        <Link
          to="/login"
          className="mt-6 inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
        >
          <ChevronLeft size={16} />
          Retour à la connexion
        </Link>
      </div>
    </div>
  );
}
